<script lang="ts" setup>
  import BvAlert from '@bv/components/alert/BvAlert.vue';
  import { useToast } from '@bv/composables';
  import { useAuthStore } from '@bv/stores/auth';
  import { storeToRefs } from 'pinia';
  import { ref, watch, onMounted } from 'vue';

  import notificationsApi from '@/api/notifications/notifications.api';
  import notificationAudio from '@/assets/audio/system-notification-02-352442.mp3';
  import { useNotificationRedirect } from '@/composables/useNotificationRedirect';
  import notificationsSocket from '@/helpers/notifications/socket';
  import { useNotificationsStore } from '@/stores/notifications/notifications.store';
  import type { notificationType } from '@/types/notifications';

  // composables
  const toast = useToast();
  const { handleRedirect } = useNotificationRedirect();

  // store
  const store = useNotificationsStore();

  const {
    notificationsNumber,
    systemNotifications,
    audioNotification,
    systemNotificationEvents,
    playNotificationAudio,
    isAssignee,
  } = storeToRefs(store);

  const {
    setPushNotifications,
    setSystemNotifications,
    setAudioNotification,
    setPushNotificationEvents,
    setSystemNotificationEvents,
    setPlayNotificationAudio,
  } = store;

  // state
  const audio = ref<HTMLAudioElement | null>(null);
  const activeNotification = ref<notificationType | null>(null);
  const alertVisible = ref(false);
  const isInitialLoad = ref(true);

  // watchers
  watch(notificationsNumber, (newVal, oldVal) => {
    if (isInitialLoad.value) {
      isInitialLoad.value = false;
      return;
    }
    if (newVal > (oldVal ?? 0)) {
      handleNewNotification();
    }
  });

  watch(playNotificationAudio, (shouldPlay) => {
    if (!shouldPlay) {
      return;
    }

    const allowed =
      audioNotification.value === 'all_conversations' ||
      (isAssignee.value && audioNotification.value === 'assigned_conversations');

    if (allowed) {
      playAudio();
    }

    setPlayNotificationAudio(false);
  });

  /* ---------------- Watchers ---------------- */
  watch(
    () => useAuthStore()?.impersonateToken || useAuthStore()?.token,
    (token) => {
      notificationsSocket.destroy();
      if (token) {
        notificationsSocket.init();
      }
    },
  );

  // methods
  const playAudio = () => {
    if (audioNotification.value !== 'off') {
      audio.value?.play();
    }

    setPlayNotificationAudio(false);
  };

  const onAlertClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    if (activeNotification.value) {
      handleRedirect(activeNotification.value);
    }
  };

  const handleNewNotification = async () => {
    try {
      const res = await notificationsApi.get({}, 'notifications?status=unread&page=1&per_page=1');

      const notification: notificationType | undefined = res?.data?.data?.[0];

      if (
        !notification ||
        !systemNotifications.value ||
        !systemNotificationEvents.value.includes(notification.type) ||
        !isAssignee.value
      ) {
        return;
      }

      activeNotification.value = notification;
      alertVisible.value = true;
    } catch (err: any) {
      toast.error(err?.message);
    }
  };

  // lifecycle
  onMounted(() => {
    if ('Notification' in window && Notification.permission !== 'granted') {
      Notification.requestPermission();
    }

    notificationsSocket?.init();

    notificationsApi.get({}, 'get-preferences').then((res) => {
      const data = res?.data?.data;

      if (!data) {
        return;
      }

      setPushNotifications(data.push_notifications);
      setSystemNotifications(data.system_notifications);
      setAudioNotification(data.audio_mode);
      setPushNotificationEvents(data.push_notification_types ?? []);
      setSystemNotificationEvents(data.system_notification_types ?? []);
    });

    audio.value = new Audio(notificationAudio);
  });
</script>

<template>
  <div class="notifications-toast-container">
    <bv-alert
      v-if="activeNotification && alertVisible"
      v-model:visible="alertVisible"
      :auto-close="6000"
      dismissible
      lines="multiple"
      :message="activeNotification.body"
      :title="activeNotification.name"
      variant="info"
      @click="onAlertClick"
    />
  </div>
</template>

<style scoped>
  .notifications-toast-container {
    position: fixed;
    inset-inline-start: 1.5rem;
    inset-block-end: 4rem;
    z-index: 9999;
    cursor: pointer;
  }
</style>
